import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentResult({ canceled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(canceled ? "canceled" : "pending");
  const [guestOrder, setGuestOrder] = useState(null);

  useEffect(() => {
    if (canceled || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (attempts++ < 20) setTimeout(poll, 2500);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, canceled]);

  // Guests (no account) get an order-specific secure download link instead of a dashboard.
  useEffect(() => {
    if (status !== "paid" || !sessionId) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/guest-order/${sessionId}`);
        const data = await res.json();
        if (data?.order) setGuestOrder(data.order);
      } catch (e) {
        /* signed-in customer flow */
      }
    })();
  }, [status, sessionId]);

  const paid = status === "paid";

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6" data-testid="payment-result">
      <div className="w-full max-w-md rounded-lg border border-navy/10 bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
        {canceled ? (
          <>
            <XCircle className="mx-auto h-14 w-14 text-navy/30" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">Payment canceled</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">No charge was made. You can start checkout again whenever you're ready.</p>
            <Link to="/app/billing" data-testid="payment-cancel-back" className="btn-gold mt-8 w-full">Back to Billing</Link>
          </>
        ) : paid && guestOrder ? (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">Your toolkit is ready</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">
              Thank you. Your payment is confirmed and the MITIVISE Essential Toolkit is ready to download
              {guestOrder.email ? <> — a secure link was also emailed to <strong>{guestOrder.email}</strong></> : ""}.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${guestOrder.download_path}`}
              data-testid="payment-guest-download"
              className="btn-gold mt-8 w-full justify-center"
            >
              Download your toolkit (.zip)
              <ArrowRight className="h-4 w-4" />
            </a>
            <p className="mt-3 text-xs text-ink/45">
              The secure link works for 30 days from purchase — the files are yours to keep forever.
            </p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">Payment received</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">
              Thank you. Your purchase is confirmed and your access has been activated. A receipt is on its way from Stripe.
            </p>
            <Link to="/app" data-testid="payment-success-continue" className="btn-gold mt-8 w-full">
              Go to your dashboard
              <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-gold" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-ink/65">This only takes a moment.</p>
          </>
        )}
        {status === "timeout" && (
          <p className="mt-4 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            Confirmation is taking longer than usual. Your access is granted automatically as soon as the payment provider confirms — check back shortly.
          </p>
        )}
      </div>
    </div>
  );
}
